import { Fill, Stroke, Style, Text } from "ol/style";
import { extractTags } from "./propertyExtraction";
import {
  ARROW_HIDE_RESOLUTION,
  getLaneDirectionArrowStyles,
  LANE_TYPES,
} from "./util/laneDirectionArrows";
import {
  BASEMAP_LAYER_ID,
  compareLaneFunction,
  getLaneFunctionOf,
  LANE_FUNCTION_LAYER_ID,
  laneFunctionColors,
} from "./util/laneFunction.mjs";

// LM Lane Function view: centre and trajectory lines coloured by `lane_function`
// (util/laneFunction.mjs), drawn like the Lanes-by-road-class view — a coloured
// line on a dark casing, direction arrows on top — so the LM views look alike.
//
// The lane carries lane_function from the basemap (29351) and from this layer
// (38434), and the view checks one against the other (compareLaneFunction):
//   agree            the function colour(s); a second function dashed over the first
//   refined          lime halo, 38434's colours: it keeps the basemap's functions
//                    and adds some (drivable -> drivable;entrance), a fine-tune.
//                    Lime because a refinement always adds a function beyond
//                    drivable, so the fill is never the drivable green under it
//   disagree         red halo; basemap colour solid, 38434 colour dashed over it
//   one side missing yellow halo; the colour of the side that has a value
//   neither          no colour: black dashes on a white casing
// Disagree, missing and neither also get a label ("29351 ≠ 38434", "no 38434",
// …) once zoomed in as far as the direction arrows show.
//
// Halos, outermost first: selection (white hover, cyan click), orange vehicle
// restriction, then the lime/red/yellow check halo, so all three stay visible together.
// Dashes use butt caps over a solid casing; round caps on both would bead.
const CASING_COLOR = "#333";
const HOVER_HALO = "white";
const CLICK_HALO = "#00b8d9";
const SELECTION_HALO_EXTRA_PX = 14;
const RESTRICTION_HALO_COLOR = "#ff9800";
const RESTRICTION_HALO_EXTRA_PX = 10;
const CHECK_HALO_EXTRA_PX = 6;
const CHECK_HALO_COLORS = {
  refined: "#76ff03",
  disagree: "#ff1744",
  "basemap-missing": "#ffd600",
  "layer-missing": "#ffd600",
};
const SECOND_FUNCTION_DASH = [6, 6];
const NONE_DASH = [3, 3];
const HOV_STRIPE = new Stroke({
  color: "white",
  width: 1,
  lineDash: [4, 4],
});
const LABEL_FONT = "bold 11px sans-serif";
// Above the arrows (zIndex 21) and lifted off the line, where a lone arrow sits.
const LABEL_Z_INDEX = 22;
const LABEL_OFFSET_Y = -12;
// Trajectory lines one pixel thinner, as in laneRoadClass.js.
const TRAJECTORY_THINNING = 1;

export function getHDLaneFunctionStyleNormal(feature, resolution) {
  return getHDLaneFunctionStyle(feature, resolution, 3);
}
export function getHDLaneFunctionStyleClick(feature, resolution) {
  return getHDLaneFunctionStyle(feature, resolution, 4, CLICK_HALO);
}
export function getHDLaneFunctionStyleHover(feature, resolution) {
  return getHDLaneFunctionStyle(feature, resolution, 4, HOVER_HALO);
}

function hasVehicleRestriction(tags) {
  return (
    tags.vehicleRestrictionBus !== undefined ||
    tags.vehicleRestrictionHGV !== undefined ||
    (tags.vehicleRestrictionMotorVehicle !== undefined &&
      tags.vehicleRestrictionMotorVehicle !== "yes") ||
    tags.vehicleRestrictionMotorcar !== undefined ||
    tags.vehicleRestrictionPSV !== undefined ||
    tags.vehicleRestrictionTaxi !== undefined
  );
}

function halo(color, width) {
  return new Style({ stroke: new Stroke({ color, width }) });
}

// Solid first colour, optional second one dashed over it.
function functionFill(color, secondColor, width) {
  const styles = [new Style({ stroke: new Stroke({ color, width }) })];
  if (secondColor !== undefined && secondColor !== color) {
    styles.push(
      new Style({
        stroke: new Stroke({
          color: secondColor,
          width,
          lineDash: SECOND_FUNCTION_DASH,
          lineCap: "butt",
        }),
      }),
    );
  }
  return styles;
}

function getHDLaneFunctionStyle(feature, resolution, width, selectionHalo) {
  if (feature.get("type") === "ORBIS_RELATION") {
    return [];
  }
  const tags = extractTags(feature);
  if (!LANE_TYPES.includes(tags.hdMap)) {
    return [];
  }
  const lineWidth =
    tags.hdMap === "lane_trajectory_line" ? width - TRAJECTORY_THINNING : width;
  const basemapValue = getLaneFunctionOf(feature, BASEMAP_LAYER_ID);
  const layerValue = getLaneFunctionOf(feature, LANE_FUNCTION_LAYER_ID);
  const { status, label } = compareLaneFunction(basemapValue, layerValue);

  const styles = [];
  if (selectionHalo) {
    styles.push(halo(selectionHalo, lineWidth + SELECTION_HALO_EXTRA_PX));
  }
  if (hasVehicleRestriction(tags)) {
    styles.push(
      halo(RESTRICTION_HALO_COLOR, lineWidth + RESTRICTION_HALO_EXTRA_PX),
    );
  }
  if (CHECK_HALO_COLORS[status] !== undefined) {
    styles.push(
      halo(CHECK_HALO_COLORS[status], lineWidth + CHECK_HALO_EXTRA_PX),
    );
  }

  if (status === "none") {
    styles.push(
      halo("white", lineWidth + 2),
      new Style({
        stroke: new Stroke({
          color: "black",
          width: lineWidth,
          lineDash: NONE_DASH,
          lineCap: "butt",
        }),
      }),
    );
  } else {
    styles.push(halo(CASING_COLOR, lineWidth + 2));
    if (status === "disagree") {
      styles.push(
        ...functionFill(
          laneFunctionColors(basemapValue)[0],
          laneFunctionColors(layerValue)[0],
          lineWidth,
        ),
      );
    } else {
      const [color, secondColor] = laneFunctionColors(
        status === "basemap-missing" || status === "refined"
          ? layerValue
          : basemapValue,
      );
      styles.push(...functionFill(color, secondColor, lineWidth));
    }
  }

  if (tags.hovLane === "yes" || tags.hov === "yes") {
    styles.push(new Style({ stroke: HOV_STRIPE }));
  }
  styles.push(
    ...getLaneDirectionArrowStyles(
      feature,
      tags.laneDirection,
      "black",
      resolution,
    ),
  );
  if (label !== undefined && resolution < ARROW_HIDE_RESOLUTION) {
    styles.push(
      new Style({
        zIndex: LABEL_Z_INDEX,
        text: new Text({
          text: label,
          font: LABEL_FONT,
          offsetY: LABEL_OFFSET_Y,
          fill: new Fill({ color: "#222" }),
          stroke: new Stroke({ color: "white", width: 3 }),
        }),
      }),
    );
  }
  return styles;
}
